import { useServices } from '@/app/providers/ServiceContext';
import { useToast } from '@/app/providers';
import { Button } from '@/core/ui/components/Button';
import type { TenantBillingPlan, TenantBillingSubscription, TenantBillingCycle } from '@yawi/types';
import { Check, Crown, Loader2, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { formatCurrency } from '@yawi/infrastructure';

interface ChangePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentSubscription: TenantBillingSubscription;
  onPlanChanged: () => void;
}

export const ChangePlanModal: React.FC<ChangePlanModalProps> = ({
  isOpen,
  onClose,
  currentSubscription,
  onPlanChanged,
}) => {
  const { billingRepo } = useServices();
  const toast = useToast();

  const [plans, setPlans] = useState<TenantBillingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState<TenantBillingCycle>(currentSubscription?.billingCycle || 'MONTHLY');
  const [changing, setChanging] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadPlans();
    }
  }, [isOpen]);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const plansData = await billingRepo.getAvailablePlans();
      setPlans(plansData);
    } catch {
      toast.error('Error al cargar los planes');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePlan = async () => {
    if (!selectedPlanId) return;

    try {
      setChanging(true);
      await billingRepo.changePlan(selectedPlanId, billingCycle);
      toast.success('Plan actualizado correctamente');
      onPlanChanged();
      onClose();
    } catch {
      toast.error('Error al cambiar el plan');
    } finally {
      setChanging(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-xl font-bold text-slate-800">Cambiar plan</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Billing Cycle Selector */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <button
              onClick={() => setBillingCycle('MONTHLY')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                billingCycle === 'MONTHLY'
                  ? 'bg-primary-500 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Mensual
            </button>
            <button
              onClick={() => setBillingCycle('YEARLY')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                billingCycle === 'YEARLY'
                  ? 'bg-primary-500 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Anual
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-3">
              {plans.map((plan) => {
                const isCurrent = plan.id === currentSubscription?.planId;
                const isSelected = plan.id === selectedPlanId;

                return (
                  <div
                    key={plan.id}
                    onClick={() => !isCurrent && setSelectedPlanId(plan.id)}
                    className={`relative p-6 rounded-xl border-2 transition-all cursor-pointer ${
                      isCurrent
                        ? 'border-slate-200 bg-slate-50 cursor-default'
                        : isSelected
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-slate-200 hover:border-primary-300'
                    }`}
                  >
                    {isCurrent && (
                      <span className="absolute -top-3 left-4 px-2 py-1 bg-slate-600 text-white text-xs font-medium rounded">
                        Plan actual
                      </span>
                    )}

                    <div className="flex items-center gap-2 mb-3">
                      <Crown className={`w-5 h-5 ${plan.code === 'ENTERPRISE' ? 'text-amber-500' : 'text-primary-500'}`} />
                      <h3 className="font-bold text-slate-800">{plan.name}</h3>
                    </div>

                    <p className="text-sm text-slate-500 mb-4">{plan.description}</p>

                    <div className="mb-4">
                      <span className="text-3xl font-bold text-slate-800">{formatCurrency(plan.basePrice)}</span>
                      <span className="text-slate-500">/mes</span>
                    </div>

                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center gap-2 text-slate-600">
                        <Check className="w-4 h-4 text-emerald-500" />
                        {plan.includedUnits} unidades
                      </li>
                      <li className="flex items-center gap-2 text-slate-600">
                        <Check className="w-4 h-4 text-emerald-500" />
                        {plan.includedUsers} usuarios
                      </li>
                      <li className="flex items-center gap-2 text-slate-600">
                        <Check className="w-4 h-4 text-emerald-500" />
                        {Math.round(plan.includedStorageBytes / (1024 * 1024 * 1024))} GB almacenamiento
                      </li>
                      <li className="flex items-center gap-2 text-slate-600">
                        <Check className="w-4 h-4 text-emerald-500" />
                        {plan.includedNotifications.toLocaleString()} notificaciones
                      </li>
                    </ul>

                    {/* Features */}
                    <div className="mt-4 pt-4 border-t space-y-2 text-sm">
                      {plan.features?.basicReports && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Check className="w-4 h-4 text-emerald-500" />
                          Reportes basicos
                        </div>
                      )}
                      {plan.features?.advancedReports && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Check className="w-4 h-4 text-emerald-500" />
                          Reportes avanzados
                        </div>
                      )}
                      {plan.features?.apiAccess && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Check className="w-4 h-4 text-emerald-500" />
                          Acceso API
                        </div>
                      )}
                      {plan.features?.prioritySupport && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Check className="w-4 h-4 text-emerald-500" />
                          Soporte prioritario
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex items-center justify-end gap-3 rounded-b-2xl">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={handleChangePlan}
            disabled={!selectedPlanId || changing}
          >
            {changing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Cambiando...
              </>
            ) : (
              'Cambiar plan'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChangePlanModal;
